import { useMemo } from 'react';
import { makeStyles, tokens, Text, Title3, Badge } from '@fluentui/react-components';
import type { ExecutiveRecommendationProps, ExecSeriesPoint, Verdict } from './execTypes';

// Structured executive recommendation card.
// Mirrors BenchmarkLab's recommendationCard visual (bordered card,
// tokens.colorNeutralBackground1, borderRadius 8px, padding 20px) so the
// substitution is visually seamless.
// Derives Winner / Best value / Fastest / Risk + Verdict entirely from props —
// fully presentational, no API calls.

const useStyles = makeStyles({
  card: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '16px',
  },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  rows: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: '12px',
  },
  rowLabel: {
    color: tokens.colorNeutralForeground2,
    fontSize: '13px',
    flexShrink: 0,
    minWidth: '100px',
  },
  rowValue: {
    fontWeight: 600,
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
    textAlign: 'right' as const,
  },
  footnote: {
    marginTop: '14px',
    paddingTop: '12px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    fontStyle: 'italic',
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
  },
  errorBanner: {
    background: tokens.colorPaletteRedBackground2,
    border: `1px solid ${tokens.colorPaletteRedBorder2}`,
    borderRadius: '6px',
    padding: '12px 16px',
    color: tokens.colorPaletteRedForeground1,
    fontWeight: 600,
    fontSize: '14px',
  },
  divider: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    margin: '10px 0',
  },
});

function deriveVerdict(winner: ExecSeriesPoint, hasErrors: boolean): Verdict {
  const allErrored = false; // handled separately at call site
  if (allErrored) return 'escalate';
  if (
    winner.overallScore >= 0.75 &&
    winner.hallucinationRisk <= 0.35 &&
    !hasErrors
  ) {
    return 'ship';
  }
  if (
    winner.overallScore < 0.5 ||
    winner.hallucinationRisk > 0.6
  ) {
    return 'escalate';
  }
  return 'test-more';
}

type BadgeColor = 'success' | 'danger' | 'warning';

function verdictBadgeColor(verdict: Verdict): BadgeColor {
  if (verdict === 'ship') return 'success';
  if (verdict === 'escalate') return 'danger';
  return 'warning';
}

function verdictBadgeLabel(verdict: Verdict, winnerLabel: string): string {
  if (verdict === 'ship') return `Ship with ${winnerLabel}`;
  if (verdict === 'escalate') return 'Escalate to humans';
  return 'Test more';
}

export function ExecutiveRecommendation({
  points,
  serverRecommendation,
  entityLabel = 'Provider',
}: ExecutiveRecommendationProps) {
  const s = useStyles();

  const allErrored = useMemo(() => points.length > 0 && points.every((p) => p.isError), [points]);
  const nonErrorPoints = useMemo(() => points.filter((p) => !p.isError), [points]);
  const errorPoints = useMemo(() => points.filter((p) => p.isError), [points]);

  const derived = useMemo(() => {
    if (nonErrorPoints.length === 0) return null;

    // Winner = highest overallScore among non-error points
    const winner = [...nonErrorPoints].sort((a, b) => b.overallScore - a.overallScore)[0];

    // Best value = cheapest point with overall >= 0.7 * winner.overall
    const valueThreshold = 0.7 * winner.overallScore;
    const valueCandidate = [...nonErrorPoints]
      .filter((p) => p.overallScore >= valueThreshold)
      .sort((a, b) => a.costUsd - b.costUsd)[0];

    // Fastest = lowest latencyMs with overall >= 0.7 * winner.overall
    const fastestCandidate = [...nonErrorPoints]
      .filter((p) => p.overallScore >= valueThreshold)
      .sort((a, b) => a.latencyMs - b.latencyMs)[0];

    // Risk flags
    const highHallucinationFlags = nonErrorPoints
      .filter((p) => p.hallucinationRisk > 0.35)
      .map((p) => `High hallucination on ${p.label}`);
    const errorFlag =
      errorPoints.length > 0
        ? [`Errors on ${errorPoints.length} ${entityLabel}`]
        : [];
    const riskFlags = [...highHallucinationFlags, ...errorFlag];

    const verdict = deriveVerdict(winner, errorPoints.length > 0);

    return { winner, valueCandidate, fastestCandidate, riskFlags, verdict };
  }, [nonErrorPoints, errorPoints, entityLabel]);

  // Zero points — render nothing per spec
  if (points.length === 0) return null;

  if (allErrored) {
    return (
      <div className={s.card}>
        <div className={s.errorBanner}>
          {`All ${entityLabel} runs errored — check provider config`}
        </div>
      </div>
    );
  }

  if (!derived) return null;

  const { winner, valueCandidate, fastestCandidate, riskFlags, verdict } = derived;
  const badgeColor = verdictBadgeColor(verdict);
  const badgeLabel = verdictBadgeLabel(verdict, winner.label);

  const winnerDisplay = `${winner.label} — ${(winner.overallScore * 100).toFixed(0)}% overall`;

  const bestValueDisplay = valueCandidate
    ? `${valueCandidate.label} — $${valueCandidate.costUsd.toFixed(5)}/query`
    : '—';

  const fastestDisplay = fastestCandidate
    ? `${fastestCandidate.label} — ${fastestCandidate.latencyMs.toFixed(0)} ms`
    : '—';

  const riskDisplay = riskFlags.length > 0 ? riskFlags.join(', ') : 'None detected';

  return (
    <div className={s.card}>
      {/* Top row: title + verdict badge */}
      <div className={s.topRow}>
        <Title3 as="h2">Recommendation</Title3>
        <Badge color={badgeColor} size="large" appearance="filled">
          {badgeLabel}
        </Badge>
      </div>

      {/* Structured 4-row list */}
      <div className={s.rows}>
        <div className={s.row}>
          <span className={s.rowLabel}>Winner</span>
          <span className={s.rowValue}>{winnerDisplay}</span>
        </div>
        <div className={s.divider} />
        <div className={s.row}>
          <span className={s.rowLabel}>Best value</span>
          <span className={s.rowValue}>{bestValueDisplay}</span>
        </div>
        <div className={s.divider} />
        <div className={s.row}>
          <span className={s.rowLabel}>Fastest</span>
          <span className={s.rowValue}>{fastestDisplay}</span>
        </div>
        <div className={s.divider} />
        <div className={s.row}>
          <span className={s.rowLabel}>Risk</span>
          <Text
            style={{
              fontWeight: 600,
              fontSize: '14px',
              color: riskFlags.length > 0
                ? tokens.colorPaletteRedForeground1
                : tokens.colorNeutralForeground1,
              textAlign: 'right',
            }}
          >
            {riskDisplay}
          </Text>
        </div>
      </div>

      {/* Server recommendation footnote */}
      {serverRecommendation && (
        <div className={s.footnote}>
          {serverRecommendation}
        </div>
      )}
    </div>
  );
}
